import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertBanner } from '@flowcore-water/ui';
import { useAuth } from '../../contexts/AuthContext';
import { attestationService } from '../../services/attestationService';
import type { Attestation } from '../../types';
import { useTheme } from '@flowcore-water/ui';

export const AttestationList: React.FC = () => {
  const { t } = useTheme();
  const { user } = useAuth();
  const [attestations, setAttestations] = useState<Attestation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;

    const loadAttestations = async () => {
      try {
        setLoading(true);
        const data = await attestationService.getForUser(user.uid);
        setAttestations(data);
        setError(null);
      } catch (err) {
        console.error('Error loading attestations:', err);
        setError('Failed to load attestations');
      } finally {
        setLoading(false);
      }
    };

    loadAttestations();
  }, [user]);

  const handleAttest = async (attestationId: string) => {
    try {
      await attestationService.attest(attestationId);
      setAttestations(
        attestations.map((att) =>
          att.id === attestationId
            ? { ...att, attested: true, attestedAt: new Date() }
            : att
        )
      );
    } catch (err) {
      console.error('Error attesting:', err);
      setError('Failed to save attestation');
    }
  };

  const pending = attestations.filter((att) => !att.attested);
  const completed = attestations.filter((att) => att.attested);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="" style={{ color: t.textMuted }}>Loading attestations...</div>
      </div>
    );
  }

  if (error) {
    return <AlertBanner variant="error" message={error} />;
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold " style={{ color: t.textPrimary }}>Training Attestations</h1>
        <p className="mt-2 " style={{ color: t.textMuted }}>
          Review and acknowledge training materials you need to complete
        </p>
      </div>

      {attestations.length === 0 ? (
        <div className="rounded-lg border   px-6 py-12 text-center">
          <p className="" style={{ color: t.textMuted }}>
            All caught up! No pending attestations.
          </p>
        </div>
      ) : (
        <>
          {pending.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-amber-400">
                Pending ({pending.length})
              </h2>
              <div className="space-y-3">
                {pending.map((attestation) => (
                  <div
                    key={attestation.id}
                    className="rounded-lg border-2 border-amber-700 bg-amber-900/20 p-6"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-amber-400">
                          {attestation.articleTitle}
                        </h3>
                        <p className="mt-1 text-sm text-amber-300">
                          Updated to v{attestation.articleVersion}
                          {attestation.lastAttestedVersion != null && (
                            <> (you last read v{attestation.lastAttestedVersion})</>
                          )}
                          {' '}• {new Date(attestation.createdAt).toLocaleDateString()}
                        </p>
                        {attestation.changeSummaries?.length > 0 && (
                          <div className="mt-2 space-y-1">
                            <p className="text-xs font-semibold uppercase tracking-wide text-amber-400/70">Changes since your last read:</p>
                            {attestation.changeSummaries.map((s, i) => (
                              <p key={i} className="text-sm text-amber-200/80 pl-3 border-l-2 border-amber-600/40">{s}</p>
                            ))}
                          </div>
                        )}
                        {attestation.linkedQualIds.length > 0 && (
                          <p className="mt-2 text-sm text-amber-200">
                            Covers {attestation.linkedQualIds.length} qualification
                            {attestation.linkedQualIds.length !== 1 ? 's' : ''}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <Link
                          to={`/library/${attestation.articleTitle
                            .toLowerCase()
                            .trim()
                            .replace(/[^\w\s-]/g, '')
                            .replace(/\s+/g, '-')
                            .replace(/-+/g, '-')}`}
                          className="rounded-lg bg-amber-600 px-4 py-2 text-center font-medium  hover:bg-amber-500 transition-colors text-sm" style={{ color: t.textPrimary }}
                        >
                          Read Article
                        </Link>
                        <button
                          onClick={() => handleAttest(attestation.id)}
                          className="rounded-lg bg-cyan-600 px-4 py-2 font-medium  hover:bg-cyan-500 transition-colors text-sm" style={{ color: t.textPrimary }}
                        >
                          I have read and understand
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {completed.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold " style={{ color: t.success }}>
                Completed ({completed.length})
              </h2>
              <div className="space-y-2">
                {completed.map((attestation) => (
                  <div
                    key={attestation.id}
                    className="rounded-lg border   p-4 flex items-center justify-between"
                  >
                    <div>
                      <p className="font-semibold " style={{ color: t.textPrimary }}>
                        {attestation.articleTitle}
                      </p>
                      <p className="text-sm " style={{ color: t.textMuted }}>
                        v{attestation.articleVersion} • Attested{' '}
                        {attestation.attestedAt?.toLocaleDateString()}
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-2 rounded-full bg-green-900/30 px-3 py-1 text-sm font-medium  border border-green-700/50" style={{ color: t.success }}>
                      ✓ Complete
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
